import { useEffect, useState, useRef } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import useApi from '../../../utils/AxiosWrapper';
import { ClipLoader } from 'react-spinners';
import { useUser } from '../../../utils/UserContext';
import SignupFooter from '../SignupFooter';
import { Entity, PartnerSignupStep } from '../../../types/apiTypes';
import { useScreenView } from '../../../utils/ScreenViewContext';
import { Input, Checkbox } from 'antd';
import { savePartnerContract } from './PartnerSignupApis';
import {
  safeSessionGetItem,
  safeSessionSetItem,
} from '../../../shared/safeStorage';

interface PartnerContractProps {
  currStep: string;
  selectedRole: Entity;
  setStep: (step: string) => void;
}

export type PartnerContractFormData = {
  signatureFullName: string;
  signatureTitle: string;
  agreementChecked: boolean;
  signedAt?: string;
};

function PartnerContract({
  currStep,
  selectedRole,
  setStep,
}: PartnerContractProps) {
  const { callProtectedApi } = useApi();
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PartnerContractFormData>({
    defaultValues: {
      signatureFullName: '',
      signatureTitle: '',
      agreementChecked: false,
    },
  });
  const { userRecord, userId } = useUser();
  const [loading, setLoading] = useState(false);
  const { screenView } = useScreenView();
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const contractRef = useRef<HTMLDivElement | null>(null);

  const agreementChecked = watch('agreementChecked');
  const signatureFullName = watch('signatureFullName');
  const signatureTitle = watch('signatureTitle');

  // Determine if submit should be disabled
  const isSubmitDisabled =
    !hasScrolledToBottom ||
    !signatureFullName ||
    !signatureTitle ||
    !agreementChecked ||
    loading;

  const handleScroll = () => {
    if (contractRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contractRef.current;
      // Check if scrolled to within 10px of bottom
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        setHasScrolledToBottom(true);
      }
    }
  };

  useEffect(() => {
    const savedData = safeSessionGetItem('partnerContract');
    if (savedData) {
      const parsedData: PartnerContractFormData = JSON.parse(savedData);
      for (const key in parsedData) {
        if (parsedData.hasOwnProperty(key)) {
          setValue(
            key as keyof PartnerContractFormData,
            parsedData[key as keyof PartnerContractFormData]
          );
        }
      }
    }
  }, [setValue]);

  const onSubmit: SubmitHandler<PartnerContractFormData> = async (data) => {
    const contractData = {
      ...data,
      signedAt: new Date().toISOString(),
    };
    safeSessionSetItem('partnerContract', JSON.stringify(contractData));

    // Save contract data to database
    await savePartnerContract(
      contractData,
      userId ?? '',
      callProtectedApi,
      setLoading,
      setStep,
      PartnerSignupStep.PartnerPendingReview
    );
  };

  const renderContractContent = () => (
    <div
      ref={contractRef}
      onScroll={handleScroll}
      className="max-h-[500px] overflow-y-auto border border-gray-300 bg-gray-50 p-6"
      style={{ borderRadius: 0 }}
    >
      <div className="space-y-6 text-sm text-gray-800">
        <div className="text-center">
          <h3 className="text-lg font-bold">
            TACH and Broker/3PL Service Agreement
          </h3>
        </div>

        <p>
          This Services Agreement (the "Agreement") is entered into this{' '}
          <span className="font-semibold">
            {new Date().toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </span>{' '}
          (the "Effective Date") by and between{' '}
          <span className="font-semibold">
            TRUCKING AUTOMATED CLEARING HOUSE, LLC
          </span>
          , a limited liability company organized and existing under the laws of
          the State of Georgia, having its principal place of business at 5
          Concourse Pkwy, #3006, Atlanta, GA 30328 (hereinafter referred to as
          "TACH") and the Broker/3PL entity identified in your business
          registration ("Broker/3PL"). TACH and Broker/3PL are collectively
          referred to herein as the "Parties" and each individually as a
          "Party."
        </p>

        <div>
          <h4 className="mb-2 font-bold">1. Purpose</h4>
          <p>
            Broker/3PL will introduce qualified carriers to TACH and promote the
            TACH Certified program. TACH will provide platform services,
            onboarding support, compliance tools, training, marketing, and
            partner resources to referred carriers.
          </p>
        </div>

        <div>
          <h4 className="mb-2 font-bold">2. Confidentiality & NDA</h4>
          <p className="mb-2">
            Both parties agree to keep all non-public information confidential,
            including carrier data, operational details, pricing, internal
            financial information, platform systems, and trade secrets.
            Confidential information may only be used to perform duties under
            this Agreement and shared only with employees who require it.
          </p>
          <p className="mb-2">
            Confidentiality does not apply to information that becomes public,
            is independently developed, already known, lawfully obtained from a
            third party, or required by law to be disclosed (with prior notice).
          </p>
          <p>
            NDA obligations survive for 3 years after service agreement
            termination.
          </p>
        </div>

        <div>
          <h4 className="mb-2 font-bold">3. TACH Responsibilities</h4>
          <ul className="ml-6 list-disc space-y-1">
            <li>
              TACH to obtain Broker's approval for any change in content of
              marketing campaigns prior to release.
            </li>
            <li>Provide onboarding, training, and ongoing partner support.</li>
            <li>
              Deliver TACH platform services to referred and certified carriers.
            </li>
            <li>
              Provide access to the TACH Partner Portal and marketing assets.
            </li>
            <li>Support Broker/3PL with co-branded outreach campaigns.</li>
            <li>Notify changes in carrier status or compliance.</li>
            <li>Maintain strong data security protocols.</li>
          </ul>
        </div>

        <div>
          <h4 className="mb-2 font-bold">4. Broker Responsibilities</h4>
          <div className="mb-3">
            <h5 className="mb-2 font-semibold">
              A. Weekly Carrier Activity Reporting & Branding Permissions:
            </h5>
            <ul className="ml-6 list-disc space-y-1">
              <li>
                Create and maintain a TACH-branded email address using the
                Broker/3PL domain (e.g., TACH@abclogistics.com). Everything sent
                from this is to Trucking companies you already do business with
                and with content previously approved by Broker. No company
                monitoring is required for this email – it's for outbound
                marketing only.
              </li>
              <li>
                Allow TACH to use Broker/3PL branding and marketing assets for
                Trucking Company acquisition and outreach.
              </li>
              <li>
                Provide TACH a weekly list of carriers who completed loads,
                including MC and contact info.
              </li>
            </ul>
          </div>
        </div>

        <div>
          <h4 className="mb-2 font-bold">5. Term & Termination</h4>
          <p className="mb-2">
            Either party may terminate with 30 days written notice.
          </p>
          <p className="mb-2">
            Upon termination, access to TACH systems ends, and confidential
            materials must be returned or destroyed.
          </p>
          <p className="mb-2">
            NDA, confidentiality, and liability protections survive termination.
          </p>
          <p>
            TACH may terminate immediately for fraud, non-compliance, or
            misconduct.
          </p>
        </div>

        <div>
          <h4 className="mb-2 font-bold">
            6. Independent Contractor Relationship
          </h4>
          <p>
            Each party acts as an independent contractor. Nothing creates a
            partnership, joint venture, or employment relationship.
          </p>
        </div>

        <div>
          <h4 className="mb-2 font-bold">
            7. Governing Law & Dispute Resolution
          </h4>
          <p className="mb-2">Georgia law governs this Agreement.</p>
          <p className="mb-2">
            All disputes will be resolved through binding arbitration in Fulton
            County, GA, under AAA commercial rules.
          </p>
          <p>
            Either party may seek emergency court relief for confidentiality or
            non-solicitation violations.
          </p>
        </div>

        <div>
          <h4 className="mb-2 font-bold">8. Entire Agreement</h4>
          <p className="mb-2">
            This service agreement is the complete understanding between the
            parties.
          </p>
          <p>
            Any modifications must be made in writing and signed by both
            parties.
          </p>
        </div>
      </div>
    </div>
  );

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
        return (
          <div className="flex h-full w-full flex-col items-center justify-start gap-y-[32px] overflow-auto py-[32px] align-middle">
            {!loading ? (
              <>
                <div className="mb-6 flex w-[70%] flex-col items-center">
                  <h2 className="text-center text-[30px] font-bold">
                    Service Agreement Review
                  </h2>
                  <p className="text-center text-gray-500">
                    Please review and sign the service agreement to continue.
                  </p>
                </div>
                {userRecord && userId && userId?.length > 0 ? (
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="w-[70%] space-y-6"
                  >
                    {renderContractContent()}

                    {!hasScrolledToBottom && (
                      <p className="text-sm text-orange-600">
                        Please scroll to the bottom of the agreement to continue
                      </p>
                    )}

                    <div className="space-y-4 border-t pt-6">
                      <h3 className="text-lg font-semibold">
                        Electronic Signature
                      </h3>
                      <p className="text-sm text-gray-600">
                        By typing your name and checking the box below, you
                        agree that this electronic signature has the same legal
                        effect as a handwritten signature.
                      </p>

                      <div className="flex w-full flex-row items-start gap-x-[16px]">
                        <div className="w-[50%]">
                          <label
                            htmlFor="signatureFullName"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Full Legal Name{' '}
                            <span className="text-red-500">*</span>
                          </label>
                          <Controller
                            name="signatureFullName"
                            control={control}
                            rules={{
                              required: 'Full legal name is required',
                              minLength: {
                                value: 2,
                                message: 'Name must be at least 2 characters',
                              },
                            }}
                            render={({ field }) => (
                              <Input
                                {...field}
                                id="signatureFullName"
                                placeholder="John Doe"
                                disabled={!hasScrolledToBottom}
                                status={errors.signatureFullName ? 'error' : ''}
                                className="mt-1 h-[38px]"
                                style={{ borderRadius: 0 }}
                              />
                            )}
                          />
                          {errors.signatureFullName && (
                            <p className="mt-1 text-sm text-red-500">
                              {errors.signatureFullName.message}
                            </p>
                          )}
                        </div>

                        <div className="w-[50%]">
                          <label
                            htmlFor="signatureTitle"
                            className="block text-sm font-medium text-gray-700"
                          >
                            Title/Position{' '}
                            <span className="text-red-500">*</span>
                          </label>
                          <Controller
                            name="signatureTitle"
                            control={control}
                            rules={{
                              required: 'Title is required',
                            }}
                            render={({ field }) => (
                              <Input
                                {...field}
                                id="signatureTitle"
                                placeholder="Owner, CEO, etc."
                                disabled={!hasScrolledToBottom}
                                status={errors.signatureTitle ? 'error' : ''}
                                className="mt-1 h-[38px]"
                                style={{ borderRadius: 0 }}
                              />
                            )}
                          />
                          {errors.signatureTitle && (
                            <p className="mt-1 text-sm text-red-500">
                              {errors.signatureTitle.message}
                            </p>
                          )}
                        </div>
                      </div>

                      <div>
                        <Controller
                          name="agreementChecked"
                          control={control}
                          rules={{
                            required: 'You must agree to the terms to continue',
                          }}
                          render={({ field }) => (
                            <Checkbox
                              {...field}
                              checked={field.value}
                              disabled={!hasScrolledToBottom}
                              onChange={(e) => field.onChange(e.target.checked)}
                              className="text-sm"
                            >
                              <span className="text-gray-700">
                                I have read and agree to the terms of this
                                Service Agreement. I understand that this
                                electronic signature is legally binding and has
                                the same effect as a handwritten signature.{' '}
                                <span className="text-red-500">*</span>
                              </span>
                            </Checkbox>
                          )}
                        />
                        {errors.agreementChecked && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.agreementChecked.message}
                          </p>
                        )}
                      </div>

                      {hasScrolledToBottom && agreementChecked && (
                        <div className="rounded bg-green-50 p-3 text-sm text-green-800">
                          <p>
                            Signed on{' '}
                            {new Date().toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      )}
                    </div>

                    <SignupFooter
                      selectedRole={selectedRole}
                      currStep={currStep}
                      disableSubmit={isSubmitDisabled}
                      onBackClick={() => setStep('partner_management_contact')}
                    />
                  </form>
                ) : (
                  <ClipLoader
                    color="#1D213B"
                    loading={true}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="h-full w-full justify-center"
                  />
                )}
              </>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loading}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="h-full w-full items-center justify-center"
              />
            )}
          </div>
        );
      case 'mobile':
        return (
          <div className="flex h-full w-full flex-col items-center justify-start gap-y-[32px] overflow-auto py-[32px] align-middle">
            {!loading ? (
              <>
                <div className="mb-6 flex w-[95%] flex-col items-center">
                  <h2 className="text-center text-[24px] font-bold">
                    Service Agreement Review
                  </h2>
                  <p className="text-center text-sm text-gray-500">
                    Please review and sign the service agreement to continue.
                  </p>
                </div>
                {userRecord && userId && userId?.length > 0 ? (
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="w-[95%] space-y-6"
                  >
                    {renderContractContent()}

                    {!hasScrolledToBottom && (
                      <p className="text-sm text-orange-600">
                        Please scroll to the bottom of the agreement to continue
                      </p>
                    )}

                    <div className="space-y-4 border-t pt-6">
                      <h3 className="text-base font-semibold">
                        Electronic Signature
                      </h3>
                      <p className="text-xs text-gray-600">
                        By typing your name and checking the box below, you
                        agree that this electronic signature has the same legal
                        effect as a handwritten signature.
                      </p>

                      <div>
                        <label
                          htmlFor="signatureFullName"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Full Legal Name{' '}
                          <span className="text-red-500">*</span>
                        </label>
                        <Controller
                          name="signatureFullName"
                          control={control}
                          rules={{
                            required: 'Full legal name is required',
                            minLength: {
                              value: 2,
                              message: 'Name must be at least 2 characters',
                            },
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="signatureFullName"
                              placeholder="John Doe"
                              disabled={!hasScrolledToBottom}
                              status={errors.signatureFullName ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                            />
                          )}
                        />
                        {errors.signatureFullName && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.signatureFullName.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="signatureTitle"
                          className="block text-sm font-medium text-gray-700"
                        >
                          Title/Position <span className="text-red-500">*</span>
                        </label>
                        <Controller
                          name="signatureTitle"
                          control={control}
                          rules={{
                            required: 'Title is required',
                          }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              id="signatureTitle"
                              placeholder="Owner, CEO, etc."
                              disabled={!hasScrolledToBottom}
                              status={errors.signatureTitle ? 'error' : ''}
                              className="mt-1 h-[38px]"
                              style={{ borderRadius: 0 }}
                            />
                          )}
                        />
                        {errors.signatureTitle && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.signatureTitle.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Controller
                          name="agreementChecked"
                          control={control}
                          rules={{
                            required: 'You must agree to the terms to continue',
                          }}
                          render={({ field }) => (
                            <Checkbox
                              {...field}
                              checked={field.value}
                              disabled={!hasScrolledToBottom}
                              onChange={(e) => field.onChange(e.target.checked)}
                              className="text-xs"
                            >
                              <span className="text-gray-700">
                                I have read and agree to the terms of this
                                Service Agreement. I understand that this
                                electronic signature is legally binding and has
                                the same effect as a handwritten signature.{' '}
                                <span className="text-red-500">*</span>
                              </span>
                            </Checkbox>
                          )}
                        />
                        {errors.agreementChecked && (
                          <p className="mt-1 text-sm text-red-500">
                            {errors.agreementChecked.message}
                          </p>
                        )}
                      </div>

                      {hasScrolledToBottom && agreementChecked && (
                        <div className="rounded bg-green-50 p-3 text-xs text-green-800">
                          <p>
                            Signed on{' '}
                            {new Date().toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      )}
                    </div>

                    <SignupFooter
                      selectedRole={selectedRole}
                      currStep={currStep}
                      disableSubmit={isSubmitDisabled}
                      onBackClick={() => setStep('partner_management_contact')}
                      isMobile={true}
                    />
                  </form>
                ) : (
                  <ClipLoader
                    color="#1D213B"
                    loading={true}
                    size={60}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                    className="h-full w-full justify-center"
                  />
                )}
              </>
            ) : (
              <ClipLoader
                color="#1D213B"
                loading={loading}
                size={60}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="h-full w-full items-center justify-center"
              />
            )}
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default PartnerContract;
